import { Column, Table } from '@tanstack/react-table';
import { clsx, type ClassValue } from 'clsx';
import { CSSProperties } from 'react';
import { twMerge } from 'tailwind-merge';

// Tailwind-safe class name merge helper
export const cn = (...inputs: ClassValue[]): string => {
  return twMerge(clsx(...inputs));
};

// Dropdown list utilities
export const parseDataInDropdown = (
  listData: any,
  mapFn: (item: any) => { value: string; label: string },
) => {
  return listData?.data?.data?.map(mapFn) || [];
};

export const parseListDataInDropdown = (
  listData: any,
  mapFn: (item: any) => { value: string; label: string },
) => {
  return listData?.data?.list?.map(mapFn) || [];
};

// Common pinned cell style
export const getCommonColumnPinningStyles = (
  column: Column<any>,
  table: Table<any>,
): CSSProperties => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const columnId = String(column.id).toLowerCase();
  const isSelectColumn =
    columnId === 'select' || columnId === 'checkbox' || columnId === 'selection';

  // On mobile <= 768px:
  // - Only the Checkbox Select column stays PINNED STICKY ON LEFT (left: 0).
  // - All other columns are unpinned so table scrolls cleanly behind it.
  const isPinned = isMobile
    ? isSelectColumn
      ? 'left'
      : false
    : column.getIsPinned();

  if (!isPinned) {
    return {
      position: 'relative',
      zIndex: 0,
      width: column.getSize(),
    };
  }

  const allLeafColumns = table?.getAllLeafColumns();
  const pinnedLeftColumns = allLeafColumns?.filter(c =>
    isMobile
      ? String(c.id).toLowerCase() === 'select'
      : c.getIsPinned() === 'left',
  );
  const pinnedRightColumns = allLeafColumns?.filter(c =>
    isMobile ? false : c.getIsPinned() === 'right',
  );
  const lastPinnedLeftColumn =
    pinnedLeftColumns?.[pinnedLeftColumns.length - 1];
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.id === pinnedRightColumns?.[0]?.id;
  const isLastLeftPinnedColumn =
    isPinned === 'left' &&
    column.id === (lastPinnedLeftColumn?.id || column.id);

  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: isPinned === 'left' ? 12 : 10,
    backgroundColor: '#ffffff',
    opacity: 1,
    width: column.getSize(),
    minWidth: column.getSize(),
    maxWidth: column.getSize(),
    borderRight: isLastLeftPinnedColumn ? '1px solid #e2e8f0' : undefined,
    borderLeft: isFirstRightPinnedColumn ? '1px solid #e2e8f0' : undefined,
    boxShadow: isLastLeftPinnedColumn
      ? '2px 0 6px rgba(0, 0, 0, 0.08)'
      : isFirstRightPinnedColumn
        ? '-2px 0 6px rgba(0, 0, 0, 0.08)'
        : undefined,
    transform: 'translateZ(0)',
    isolation: 'isolate',
  };
};

// Common pinned header style
export const getCommonColumnPinningStylesForHeader = (
  column: Column<any>,
  table: Table<any>,
): CSSProperties => {
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;
  const columnId = String(column.id).toLowerCase();
  const isSelectColumn =
    columnId === 'select' || columnId === 'checkbox' || columnId === 'selection';

  const isPinned = isMobile
    ? isSelectColumn
      ? 'left'
      : false
    : column.getIsPinned();

  if (!isPinned) {
    return {
      position: 'relative',
      zIndex: 1,
      width: column.getSize(),
      backgroundColor: '#f8fafc',
    };
  }

  const allLeafColumns = table?.getAllLeafColumns();
  const pinnedLeftColumns = allLeafColumns?.filter(c =>
    isMobile
      ? String(c.id).toLowerCase() === 'select'
      : c.getIsPinned() === 'left',
  );
  const pinnedRightColumns = allLeafColumns?.filter(c =>
    isMobile ? false : c.getIsPinned() === 'right',
  );
  const lastPinnedLeftColumn =
    pinnedLeftColumns?.[pinnedLeftColumns.length - 1];
  const isFirstRightPinnedColumn =
    isPinned === 'right' && column.id === pinnedRightColumns?.[0]?.id;
  const isLastLeftPinnedColumn =
    isPinned === 'left' &&
    column.id === (lastPinnedLeftColumn?.id || column.id);

  return {
    position: 'sticky',
    left: isPinned === 'left' ? `${column.getStart('left')}px` : undefined,
    right: isPinned === 'right' ? `${column.getAfter('right')}px` : undefined,
    zIndex: isPinned === 'left' ? 15 : 13,
    backgroundColor: '#f8fafc',
    opacity: 1,
    width: column.getSize(),
    minWidth: column.getSize(),
    maxWidth: column.getSize(),
    borderRight: isLastLeftPinnedColumn ? '1px solid #e2e8f0' : undefined,
    borderLeft: isFirstRightPinnedColumn ? '1px solid #e2e8f0' : undefined,
    boxShadow: isLastLeftPinnedColumn
      ? '2px 0 6px rgba(0, 0, 0, 0.08)'
      : isFirstRightPinnedColumn
        ? '-2px 0 6px rgba(0, 0, 0, 0.08)'
        : undefined,
    transform: 'translateZ(0)',
    isolation: 'isolate',
  };
};
